// Settings → Routes → :id editor.

import { useState } from "react";
import { useAppData } from "../store";
import {
  Btn, Tabs, TextArea, TextInput, Toggle,
} from "../components/Primitives";
import { Ic } from "../components/Icon";
import { MultMini } from "../components/Multiplier";
import { Select } from "../components/Select";

interface Props {
  id: string;
  onBack: () => void;
  onEditField: (id: string) => void;
  onEditRule:  (id: string | "new") => void;
}

export function RouteDetailScreen({ id, onBack, onEditField, onEditRule }: Props) {
  const { data, saveConfig } = useAppData();
  const route = data?.config.routes.find((r) => r.id === id) ?? data?.config.routes[0];
  const [tab, setTab] = useState("Fields");
  const [name, setName]   = useState(route?.name ?? "");
  const [desc, setDesc]   = useState(route?.desc ?? "");
  const [isDef, setIsDef] = useState(route?.default ?? false);
  const [owner, setOwner] = useState(route?.owner ?? "");
  const [saving, setSaving] = useState(false);

  if (!data || !route) return null;

  const onSave = async () => {
    setSaving(true);
    try {
      await saveConfig({
        ...data.config,
        routes: data.config.routes.map((r) => r.id === route.id
          ? { ...r, name, desc, default: isDef, owner: owner || null }
          : (isDef ? { ...r, default: false } : r),
        ),
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div className="topbar">
        <span className="crumb" onClick={onBack} style={{ cursor: "pointer" }}>Routes</span>
        <span className="crumb-sep">/</span>
        <h1>{name}</h1>
        {isDef && <span className="pill" style={{ fontSize: 10 }}>Default</span>}
        <span className="grow" />
        <Btn size="sm" ico="check" kind="primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Btn>
      </div>
      <div style={{ padding: "0 28px", background: "var(--bg)" }}>
        <Tabs tabs={["Overview", "Fields", "Rules", "Enrichments"]} value={tab} onChange={setTab} />
      </div>
      <div className="scroll" style={{ padding: "22px 28px 60px" }}>
        {tab === "Overview" && (
          <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label className="field-label" style={Label}>Name</label>
              <TextInput value={name} onChange={setName} />
            </div>
            <div>
              <label className="field-label" style={Label}>Description</label>
              <TextArea rows={3} value={desc} onChange={setDesc} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle on={isDef} onChange={setIsDef} />
              <span style={{ fontSize: 13 }}>Use this as the default route for unmatched inbound</span>
            </div>
            <div>
              <label className="field-label" style={Label}>Auto-assign owner</label>
              <Select
                value={owner}
                onChange={setOwner}
                placeholder="No auto-assignment"
                options={[
                  { value: "", label: "No auto-assignment" },
                  ...(data.config.team ?? [])
                    .filter((m) => m.status !== "disabled")
                    .map((m) => ({ value: m.id, label: m.name })),
                ]}
              />
              <div style={{ color: "var(--ink-3)", fontSize: 11.5, marginTop: 6 }}>
                New deals on this route land directly on this member.
              </div>
            </div>
          </div>
        )}
        {tab === "Fields" && <RouteFields onEdit={onEditField} />}
        {tab === "Rules"  && <RouteRules  onEdit={onEditRule} />}
        {tab === "Enrichments" && <RouteEnrichments />}
      </div>
    </div>
  );
}

const Label: React.CSSProperties = {
  fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 500, display: "block", marginBottom: 6,
};

export function RouteFields({ onEdit }: { onEdit: (id: string) => void }) {
  const { data } = useAppData();
  const fields = data?.config.fields ?? [];
  return (
    <>
      <div className="h-section">
        <h3>Fields · {fields.length}</h3>
        <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
          · these are what Prism extracts from every inbound
        </span>
        <span className="grow" style={{ flex: 1 }} />
        <Btn size="sm" ico="plus" onClick={() => onEdit("new")}>Add field</Btn>
      </div>
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 8, overflow: "hidden",
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "22px 1.4fr 100px 100px 1.2fr 50px",
          padding: "11px 16px", fontSize: 10.5, color: "var(--ink-3)",
          textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 500,
          borderBottom: "1px solid var(--border)", background: "var(--bg)",
        }}>
          <span /><span>Name</span><span>Type</span><span>Required</span><span>Source</span><span />
        </div>
        {fields.map((f, i) => (
          <div key={(f as any).key ?? f.id ?? i} className="row" style={{
            display: "grid",
            gridTemplateColumns: "22px 1.4fr 100px 100px 1.2fr 50px",
            padding: "12px 16px",
            borderBottom: i < fields.length - 1 ? "1px solid var(--border)" : "none",
            alignItems: "center", fontSize: 13,
          }} onClick={() => onEdit((f as any).key ?? f.id ?? String(i))}>
            <span style={{ color: "var(--ink-4)", cursor: "grab" }}>
              <Ic name="dots" size={14} />
            </span>
            <span style={{ fontWeight: 500 }}>{(f as any).key ?? f.label}</span>
            <span style={{ color: "var(--ink-2)" }}>{f.type}</span>
            <span style={{ color: f.required ? "var(--ink)" : "var(--ink-3)" }}>
              {f.required ? "Yes" : "—"}
            </span>
            <span style={{ color: "var(--ink-2)" }}>{f.source}</span>
            <span style={{ color: "var(--ink-3)", textAlign: "right" }}>
              <Ic name="chevR" size={14} />
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

export function RouteRules({ onEdit }: { onEdit: (id: string | "new") => void }) {
  const { data } = useAppData();
  const rules = data?.config.rules ?? [];
  return (
    <>
      <div className="h-section">
        <h3>Rules · {rules.length}</h3>
        <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
          · each rule contributes 10 × its multiplier when it passes
        </span>
        <span className="grow" style={{ flex: 1 }} />
        <Btn size="sm" ico="plus" onClick={() => onEdit("new")}>Add rule</Btn>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rules.map((rule) => (
          <div key={rule.id} onClick={() => onEdit(String(rule.id))} style={{
            display: "grid",
            gridTemplateColumns: "1.8fr 170px 80px 30px",
            gap: 18, padding: "14px 18px",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8, alignItems: "center", cursor: "pointer",
          }}>
            <div>
              <div style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>{rule.field}</span>
                <span style={{ color: "var(--ink-3)", margin: "0 8px" }}>{rule.op}</span>
                <span>{Array.isArray(rule.value)
                  ? (rule.value as any[]).join(", ")
                  : (typeof rule.value === "number"
                      ? rule.value.toLocaleString()
                      : String(rule.value))}</span>
              </div>
              <div style={{ color: "var(--ink-3)", fontSize: 11.5, marginTop: 3 }}>{rule.desc}</div>
            </div>
            <MultMini value={rule.multiplier} />
            {rule.hard
              ? <span className="pill rejected" style={{ fontSize: 10 }}>Hard filter</span>
              : <span style={{ color: "var(--ink-3)", fontSize: 11 }}>Soft</span>}
            <span style={{ color: "var(--ink-3)", textAlign: "right" }}>
              <Ic name="chevR" size={14} />
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function RouteEnrichments() {
  const { data, saveConfig } = useAppData();
  const enrichments = data?.config.enrichments ?? [];
  const toggle = async (id: string) => {
    if (!data) return;
    await saveConfig({
      ...data.config,
      enrichments: data.config.enrichments.map((e: any) =>
        e.id === id ? { ...e, status: e.status === "active" ? "not_configured" : "active" } : e,
      ) as any,
    });
  };
  return (
    <>
      <div className="h-section">
        <h3>Enrichments</h3>
        <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
          · toggle which third-party sources run on this route
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {enrichments.map((e: any) => (
          <div key={e.id} style={{
            padding: 14, background: "var(--surface)",
            border: "1px solid var(--border)", borderRadius: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 500, fontSize: 13 }}>{e.name}</span>
              <span style={{ flex: 1 }} />
              <Toggle on={e.status === "active"} onChange={() => toggle(e.id)} />
            </div>
            <div style={{ color: "var(--ink-2)", fontSize: 11.5, marginTop: 4 }}>{e.desc}</div>
          </div>
        ))}
      </div>
    </>
  );
}
